import dayjs from 'dayjs';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';

/**
 * Export deviation report to PDF
 * Clean, professional design matching form structure
 */
export const exportDeviationToPdf = async (deviation, includeAttachments = false) => {
  let missingFilesCount = 0;
  try {
    // Dynamic imports to avoid circular dependencies
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { deviationConfig } = await import('../deviationConfig');
    const { API_BASE_URL, API_ENDPOINTS } = await import('../../../config/apiConfig');
    
    pdfMake.default.vfs = pdfFonts.vfs || pdfFonts.default || {};

    // Define logo image key for pdfMake
    const logoImageKey = 'companyLogo';

    // Fetch company logo from application settings
    let companyLogoBase64 = null;
    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(`${API_BASE_URL}/application-setting/key/companyLogo`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting && logoSetting.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          
          // Construct full URL if not already absolute
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/') 
              ? `${baseUrl}${logoUrl}` 
              : `${baseUrl}/${logoUrl}`;
          }

          // Fetch logo and convert to base64
          const logoResponse = await fetch(logoUrl, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });

          if (logoResponse.ok) {
            // Check if it's SVG first
            const isSvg = logoUrl.toLowerCase().endsWith('.svg');
            
            if (isSvg) {
              // Convert SVG to PNG for pdfMake compatibility
              const svgText = await logoResponse.text();
              companyLogoBase64 = await new Promise((resolve, reject) => {
                const img = new Image();
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                  try {
                    const maxWidth = 300;
                    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    
                    const pngDataUrl = canvas.toDataURL('image/png');
                    const base64data = pngDataUrl.split(',')[1];
                    
                    URL.revokeObjectURL(url);
                    resolve({
                      data: base64data,
                      mimeType: 'image/png'
                    });
                  } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                  }
                };
                
                img.onerror = () => {
                  URL.revokeObjectURL(url);
                  reject(new Error('Failed to load SVG image'));
                };
                
                img.src = url;
              });
            } else {
              // For non-SVG images
              const clonedResponse = logoResponse.clone();
              const logoBlob = await clonedResponse.blob();
              companyLogoBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64data = reader.result.split(',')[1];
                  resolve({
                    data: base64data,
                    mimeType: logoBlob.type || 'image/png'
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(logoBlob);
              });
            }
          }
        }
      }
    } catch (error) {
      console.warn('Error fetching company logo for PDF:', error);
    }

    // Helper function to fetch file as base64
    const fetchFileAsBase64 = async (fileId, fileName) => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const response = await fetch(API_ENDPOINTS.fileAttachment.download(fileId), {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (!response.ok) {
          return null;
        }

        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result.split(',')[1];
            resolve({
              data: base64data,
              fileName: fileName,
              mimeType: blob.type || 'application/octet-stream'
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error(`Error fetching file ${fileName}:`, error);
        return null;
      }
    };

    // Fetch file attachments if needed
    const fileAttachmentsData = [];
    if (includeAttachments && deviation.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const filesResponse = await fetch(`${API_BASE_URL}/file-attachment/entity/${deviationConfig.entityTypeId || 8}/${deviation.id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (filesResponse.ok) {
          const files = await filesResponse.json();
          const activeFiles = files.filter(file => !file.isDeleted);
          
          for (const file of activeFiles) {
            const fileData = await fetchFileAsBase64(file.id, normalizeFileName(file.fileName || ''));
            if (fileData) {
              fileAttachmentsData.push({
                ...fileData,
                originalFile: file
              });
            } else {
              missingFilesCount++;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching file attachments:', error);
      }
    }

    // Fetch approval requests
    let approvalRequests = [];
    if (deviation.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const url = `${API_BASE_URL}/approval-requests/entity/deviation/${deviation.id}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });
        
        if (response.ok) {
          const fetchedData = await response.json();
          if (Array.isArray(fetchedData)) {
            approvalRequests = fetchedData;
          }
        }
      } catch (error) {
        console.error('Error fetching approval requests for PDF:', error);
      }
    }

    // Keep only the latest approval request (last Review and Approval)
    const latestApprovalRequest = Array.isArray(approvalRequests) && approvalRequests.length > 0
      ? approvalRequests[0] // Backend already returns sorted by createdAt DESC
      : null;

    // Merge latest approval request into deviation object
    const deviationWithApprovals = {
      ...deviation,
      approvalRequests: latestApprovalRequest ? [latestApprovalRequest] : []
    };

    // Helper functions
    const formatDate = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return date;
      }
    };

    const formatDateOnly = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return date;
      }
    };

    const formatBoolean = (value) => {
      if (value === true) return 'Yes';
      if (value === false) return 'No';
      return 'N/A';
    };

    const formatArray = (arr, field = 'name') => {
      if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
      return arr.map(item => item[field] || item.name || item).join(', ');
    };

    const deviationData = deviationWithApprovals;

    const { fetchDeviationReferenceDocument, formatDeviationReferenceDocumentText } =
      await import('./fetchDeviationReferenceDocument');
    let referenceDocumentText = deviationData.referenceDocument || null;
    if (!referenceDocumentText) {
      const referenceDocument = await fetchDeviationReferenceDocument();
      referenceDocumentText = formatDeviationReferenceDocumentText(referenceDocument);
    }

    const referenceDocumentLine = {
      text: referenceDocumentText || 'N/A',
      style: 'reportSubtitle',
      alignment: 'right',
      margin: [0, 0, 0, 5],
    };

    // Build header content with logo on first page
    const headerContent = [];
    if (companyLogoBase64) {
      headerContent.push({
        columns: [
          {
            image: logoImageKey,
            width: 200,
            alignment: 'left'
          },
          {
            stack: [
              {
                text: 'DEVIATION REPORT',
                style: 'reportTitle',
                alignment: 'right',
                margin: [0, 0, 0, 5]
              },
              referenceDocumentLine
            ],
            alignment: 'right'
          }
        ],
        margin: [0, 0, 0, 20]
      });
    } else {
      headerContent.push(
        {
          text: 'DEVIATION REPORT',
          style: 'reportTitle',
          alignment: 'center',
          margin: [0, 0, 0, 10],
        },
        {
          ...referenceDocumentLine,
          alignment: 'center',
        },
      );
    }

    // Build content sections
    const content = [
      // Header
      {
        stack: headerContent
      },
      // Basic Information
      {
        text: '1. BASIC INFORMATION',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Deviation Code:', style: 'fieldLabel', width: '22%' },
          { text: deviationData.code || 'N/A', style: 'fieldValue', width: '38%' },
          { text: 'Status:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.status?.name || 'N/A', style: 'fieldValue', width: '25%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Title:', style: 'fieldLabel', width: '22%' },
          { text: deviationData.title || 'N/A', style: 'fieldValue', width: '78%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Description:', style: 'fieldLabel', width: '22%' },
          { text: deviationData.description || 'N/A', style: 'fieldValue', width: '78%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Discovery & Reporting
      {
        text: '2. DISCOVERY & REPORTING',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Discovered Date:', style: 'fieldLabel', width: '22%' },
          { text: formatDate(deviationData.discoveredAt), style: 'fieldValue', width: '38%' },
          { text: 'Discovered By Depositor:', style: 'fieldLabel', width: '22%' },
          { text: deviationData.discoveredBy?.name || 'N/A', style: 'fieldValue', width: '18%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Reported Date:', style: 'fieldLabel', width: '22%' },
          { text: formatDate(deviationData.reportedAt), style: 'fieldValue', width: '38%' },
          { text: 'Reported By:', style: 'fieldLabel', width: '22%' },
          {
            text:
              deviationData.reportedByName ||
              deviationData.reportedBy?.userDisplayName ||
              deviationData.reportedBy?.name ||
              'N/A',
            style: 'fieldValue',
            width: '18%',
          }
        ],
        margin: [0, 0, 0, 15]
      },
      // Products
      ...(deviationData.products && deviationData.products.length > 0 ? [{
        text: '3. PRODUCTS',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      }, {
        table: {
          headerRows: 1,
          widths: ['auto', '*', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: 'Product Code', style: 'tableHeader' },
              { text: 'Product Name', style: 'tableHeader' },
              { text: 'Batch No.', style: 'tableHeader' },
              { text: 'Quantity', style: 'tableHeader' },
              { text: 'Expiry Date', style: 'tableHeader' }
            ],
            ...deviationData.products.map(product => [
              { text: product.productCode || 'N/A', style: 'tableCell' },
              { text: product.productName || 'N/A', style: 'tableCell' },
              { text: product.batchNumber || 'N/A', style: 'tableCell' },
              { text: product.productQuantity?.toString() || 'N/A', style: 'tableCell' },
              { text: formatDateOnly(product.expiryDate), style: 'tableCell' }
            ])
          ]
        },
        layout: 'lightHorizontalLines',
        margin: [0, 5, 0, 15]
      }] : []),
      // Summary of Investigation Results
      {
        text: '4. SUMMARY OF INVESTIGATION RESULTS INTO THE CAUSES OF THE DEVIATION',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Cause Categorization:', style: 'fieldLabel', width: '35%' },
          { text: formatArray(deviationData.rootCauses), style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Cause Categorization Note:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.rootCauseNote || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Department Head Date:', style: 'fieldLabel', width: '35%' },
          { text: formatDateOnly(deviationData.departmentHeadInvestigation), style: 'fieldValue', width: '15%' },
          { text: 'Department Head By:', style: 'fieldLabel', width: '20%' },
          { text: deviationData.departmentHeadInvestigationBy?.userDisplayName || deviationData.departmentHeadInvestigationBy?.name || 'N/A', style: 'fieldValue', width: '30%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'QA Manager Date:', style: 'fieldLabel', width: '35%' },
          { text: formatDateOnly(deviationData.qaManagerInvestigation), style: 'fieldValue', width: '15%' },
          { text: 'QA Manager By:', style: 'fieldLabel', width: '20%' },
          { text: deviationData.qaManagerInvestigationBy?.userDisplayName || deviationData.qaManagerInvestigationBy?.name || 'N/A', style: 'fieldValue', width: '30%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Assessment of Criticality and Impact
      {
        text: '5. ASSESSMENT OF CRITICALITY AND IMPACT',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Criticality Categorization:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.criticality?.name || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Notification of Customer:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.customerImpact), style: 'fieldValue', width: '15%' },
          { text: 'Note:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.customerImpactNote || 'N/A', style: 'fieldValue', width: '35%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Impact on Product:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.impactOnProduct), style: 'fieldValue', width: '15%' },
          { text: 'Note:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.impactOnProductNote || 'N/A', style: 'fieldValue', width: '35%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Impact on Other Batches:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.impactOnOtherBatches), style: 'fieldValue', width: '15%' },
          { text: 'Note:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.impactOnOtherBatchesNote || 'N/A', style: 'fieldValue', width: '35%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Impact on Other Products:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.impactOnOtherProducts), style: 'fieldValue', width: '15%' },
          { text: 'Note:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.impactOnOtherProductsNote || 'N/A', style: 'fieldValue', width: '35%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Impact on Other Systems:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.impactOnOtherSystems), style: 'fieldValue', width: '15%' },
          { text: 'Note:', style: 'fieldLabel', width: '15%' },
          { text: deviationData.impactOnOtherSystemsNote || 'N/A', style: 'fieldValue', width: '35%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Final Product Disposition
      {
        text: '6. FINAL PRODUCT DISPOSITION (FOR PRODUCT-RELATED DEVIATIONS)',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Final Disposition:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.finalDisposition?.name || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 5]
      },
      ...(deviationData.finalDispositionOtherNote ? [{
        columns: [
          { text: 'Other Note:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.finalDispositionOtherNote, style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 15]
      }] : [{ text: '', margin: [0, 0, 0, 15] }]),
      // Risk Assessment
      {
        text: '7. RISK ASSESSMENT',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Risk Assessment Required:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.riskAssessmentRequired), style: 'fieldValue', width: '15%' },
          { text: 'Risk Assessment:', style: 'fieldLabel', width: '20%' },
          { text: deviationData.riskAssessment?.code || deviationData.riskAssessment?.title || 'N/A', style: 'fieldValue', width: '30%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'RA Completed At:', style: 'fieldLabel', width: '35%' },
          { text: formatDate(deviationData.riskAssessmentCompletedAt), style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // CAPA Actions
      ...(deviationData.deviationActions && deviationData.deviationActions.length > 0 ? [{
        text: '8. CORRECTIVE AND PREVENTIVE ACTIONS (CAPA)',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      }, {
        table: {
          headerRows: 1,
          widths: ['auto', '*', 'auto', 'auto', 'auto'],
          body: [
            [
              { text: 'Action Type', style: 'tableHeader' },
              { text: 'Description', style: 'tableHeader' },
              { text: 'Responsible', style: 'tableHeader' },
              { text: 'Due Date', style: 'tableHeader' },
              { text: 'Close Date', style: 'tableHeader' }
            ],
            ...deviationData.deviationActions.map(action => [
              { text: action.actionType?.name || 'N/A', style: 'tableCell' },
              { text: action.description || 'N/A', style: 'tableCell' },
              { text: action.responsible || 'N/A', style: 'tableCell' },
              { text: formatDateOnly(action.dueDate), style: 'tableCell' },
              { text: formatDateOnly(action.closeDate), style: 'tableCell' }
            ])
          ]
        },
        layout: 'lightHorizontalLines',
        margin: [0, 5, 0, 15]
      }] : []),
      // Implementation
      {
        text: '9. IMPLEMENTATION',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'All required actions have been completed:', style: 'fieldLabel', width: '50%' },
          { text: formatBoolean(deviationData.implementation), style: 'fieldValue', width: '15%' },
          { text: 'Implementation Completed At:', style: 'fieldLabel', width: '20%' },
          { text: formatDate(deviationData.implementationCompletedAt), style: 'fieldValue', width: '15%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Implementation Review Required
      {
        text: '10. IMPLEMENTATION REVIEW REQUIRED',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Implementation Review Required:', style: 'fieldLabel', width: '35%' },
          { text: formatBoolean(deviationData.implementationEvaluation), style: 'fieldValue', width: '15%' },
          { text: 'Implementation Evaluation At:', style: 'fieldLabel', width: '25%' },
          { text: formatDate(deviationData.implementationEvaluationAt), style: 'fieldValue', width: '25%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Implementation Evaluation Note:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.implementationEvaluationNote || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Implementation Evaluation By:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.implementationEvaluationBy?.userDisplayName || deviationData.implementationEvaluationBy?.name || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Implementation Evaluation (ΠΑΡ.2)
      {
        text: '11. IMPLEMENTATION EVALUATION',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Required:', style: 'fieldLabel', width: '35%' },
          {
            text: deviationData.implementationEvaluationRequiredMonths
              ? `${deviationData.implementationEvaluationRequiredMonths} months`
              : 'N/A',
            style: 'fieldValue',
            width: '65%',
          },
        ],
        margin: [0, 0, 0, 15]
      },
      // Closure
      {
        text: '12. CLOSURE',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      },
      {
        columns: [
          { text: 'Closed At:', style: 'fieldLabel', width: '35%' },
          { text: formatDate(deviationData.closedAt), style: 'fieldValue', width: '15%' },
          { text: 'Closed By:', style: 'fieldLabel', width: '20%' },
          { text: deviationData.closedBy?.userDisplayName || deviationData.closedBy?.name || 'N/A', style: 'fieldValue', width: '30%' }
        ],
        margin: [0, 0, 0, 5]
      },
      {
        columns: [
          { text: 'Closure Comments:', style: 'fieldLabel', width: '35%' },
          { text: deviationData.closureComments || 'N/A', style: 'fieldValue', width: '65%' }
        ],
        margin: [0, 0, 0, 15]
      },
      // Review and Approval Status
      ...(deviationData.approvalRequests && deviationData.approvalRequests.length > 0 ? [{
        text: '12. REVIEW AND APPROVAL STATUS',
        style: 'sectionTitle',
        margin: [0, 10, 0, 10]
      }, ...deviationData.approvalRequests.map((approval, idx) => [
        {
          text: `Approval Request ${idx + 1}: ${approval.code || approval.id || 'N/A'}`,
          style: 'subsectionTitle',
          margin: [0, idx > 0 ? 15 : 0, 0, 8]
        },
        {
          columns: [
            { text: 'Status:', style: 'fieldLabel', width: '35%' },
            { text: approval.status?.name || 'N/A', style: 'fieldValue', width: '65%' }
          ],
          margin: [0, 0, 0, 5]
        },
        {
          columns: [
            { text: 'Requested By:', style: 'fieldLabel', width: '35%' },
            { text: approval.requestedBy?.userDisplayName || approval.requestedBy?.name || 'N/A', style: 'fieldValue', width: '65%' }
          ],
          margin: [0, 0, 0, 5]
        },
        {
          columns: [
            { text: 'Requested Date:', style: 'fieldLabel', width: '35%' },
            { text: formatDate(approval.requestedAt), style: 'fieldValue', width: '65%' }
          ],
          margin: [0, 0, 0, 5]
        },
        ...(approval.steps && approval.steps.length > 0 ? [{
          text: 'Approval Steps:',
          style: 'subsectionTitle',
          margin: [0, 10, 0, 8]
        }, {
          table: {
            headerRows: 1,
            widths: ['auto', '*', '*', '*', 'auto', '*'],
            body: [
              [
                { text: 'Step', style: 'tableHeader', alignment: 'center' },
                { text: 'Step Name', style: 'tableHeader' },
                { text: 'Type', style: 'tableHeader' },
                { text: 'Status', style: 'tableHeader' },
                { text: 'Approved Date', style: 'tableHeader' },
                { text: 'Approver/Reviewer', style: 'tableHeader' }
              ],
              ...approval.steps.map(step => [
                { text: step.stepNumber?.toString() || 'N/A', style: 'tableCell', alignment: 'center' },
                { text: step.stepName || 'N/A', style: 'tableCell' },
                { text: step.stepType || 'N/A', style: 'tableCell' },
                { text: step.status?.name || 'Pending', style: 'tableCell' },
                { text: step.approvedAt ? formatDate(step.approvedAt) : (step.completedAt ? formatDate(step.completedAt) : 'N/A'), style: 'tableCell' },
                {
                  text: [
                    step.approvedBy?.userDisplayName || step.approvedBy?.name,
                    step.reviewedBy?.userDisplayName || step.reviewedBy?.name,
                    step.assignedApprover?.userDisplayName || step.assignedApprover?.name
                  ].filter(Boolean).join(' / ') || 'N/A',
                  style: 'tableCell'
                }
              ])
            ]
          },
          layout: 'lightHorizontalLines',
          margin: [0, 5, 0, 10]
        }] : [])
      ]).flat()] : [])
    ];

    // PDF Document Definition
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
        lineHeight: 1.4,
        color: '#333333'
      },
      header: function(currentPage, pageCount, pageSize) {
        // Add logo to top left of every page except the first page
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 120,
            alignment: 'left',
            margin: [40, 20, 0, 0]
          };
        }
        return null;
      },
      footer: function(currentPage, pageCount) {
        return {
          text: `Page ${currentPage} of ${pageCount} | Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
          alignment: 'center',
          fontSize: 8,
          color: '#999999',
          margin: [0, 10, 0, 0]
        };
      },
      content: content,
      images: companyLogoBase64 ? {
        [logoImageKey]: `data:${companyLogoBase64.mimeType};base64,${companyLogoBase64.data}`
      } : {},
      styles: {
        reportTitle: {
          fontSize: 18,
          bold: true,
          color: '#1a1a1a'
        },
        reportSubtitle: {
          fontSize: 12,
          color: '#666666'
        },
        sectionTitle: {
          fontSize: 14,
          bold: true,
          color: '#2c3e50',
          decoration: 'underline'
        },
        subsectionTitle: {
          fontSize: 12,
          bold: true,
          color: '#34495e'
        },
        fieldLabel: {
          fontSize: 10,
          bold: true,
          color: '#555555'
        },
        fieldValue: {
          fontSize: 10,
          color: '#333333'
        },
        tableHeader: {
          fontSize: 9,
          bold: true,
          color: '#ffffff',
          fillColor: '#34495e'
        },
        tableCell: {
          fontSize: 9,
          color: '#333333'
        }
      }
    };

    // Generate PDF
    const pdfDoc = pdfMake.default.createPdf(docDefinition);
    
    const result = { missingFilesCount };
    
    if (includeAttachments && fileAttachmentsData.length > 0) {
      // Create a ZIP file containing the PDF and all attachments
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        
        pdfDoc.getBlob((pdfBlob) => {
          zip.file(`Deviation-${deviationData.code || deviationData.id}.pdf`, pdfBlob);
          
          fileAttachmentsData.forEach((fileData) => {
            const { data, fileName } = fileData;
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            zip.file(fileName, bytes);
          });
          
          zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Deviation-${deviationData.code || deviationData.id}-with-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }).catch((error) => {
            console.error('Error generating ZIP file:', error);
            pdfDoc.download(`Deviation-${deviationData.code || deviationData.id}.pdf`);
          });
        });
      } catch (error) {
        console.error('Error importing JSZip or creating ZIP:', error);
        pdfDoc.download(`Deviation-${deviationData.code || deviationData.id}.pdf`);
      }
    } else {
      // Just download the PDF
      pdfDoc.download(`Deviation-${deviationData.code || deviationData.id}.pdf`);
    }
    
    return result;
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

